import api from '@common/api';
import { uuidRegEx } from '@common/utility/validations';
import { AssetClass, SubscriptionOffer } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

interface PropertiesModelProps {
  properties: SubscriptionOffer[];
  property: SubscriptionOffer | null;
}

interface PropertiesModelActions {
  fetchProperties: (
    investorAccountID: string | null,
  ) => Promise<{ error?: string; success: boolean; properties?: SubscriptionOffer[] }>;
  fetchProperty: (id: string, investorAccountID: string | null) => Promise<SubscriptionOffer | null | undefined>;
}

function propertySortScore(subscriptionOffer: SubscriptionOffer) {
  if (
    !subscriptionOffer?.target_amount ||
    Number(subscriptionOffer.raised_amount?.total) === subscriptionOffer.target_amount
  ) {
    return Infinity;
  } else {
    return subscriptionOffer.target_amount - Number(subscriptionOffer.raised_amount?.total || 0);
  }
}

export const PropertiesModel = createStore(
  persist<PropertiesModelProps & PropertiesModelActions>(
    (set) => ({
      fetchProperties: async (investorAccountID) => {
        try {
          const properties = await api.listSubscriptionOffers({
            asset_class: AssetClass.SFR,
            investor_account_id: investorAccountID ?? null,
          });
          if (properties) {
            // Sort the properties by the ones that should be presented/marketed first
            // return subscriptionOffers
            //   .filter((x) => x.offering?.product_family === 'HOME_SHARES')
            //   .sort((a, b) => propertySortScore(a) - propertySortScore(b))
            const sortedProperties = properties.sort((a, b) => propertySortScore(a) - propertySortScore(b));
            set({ properties: sortedProperties });
          }
          return { properties, success: true };
        } catch {
          return { error: 'Unable to fetch property list', success: false };
        }
      },
      fetchProperty: async (slug, investorAccountID) => {
        if (!slug) return null;

        // Fetch the subscriptionOffer by ID if the slug appears to be a UUID
        const subscriptionOffer = uuidRegEx.test(slug)
          ? await api.getSubscriptionOffer({ id: slug, investor_account_id: investorAccountID ?? null })
          : await api.getSubscriptionOffer({ investor_account_id: investorAccountID ?? null, slug });
        if (subscriptionOffer) set({ property: subscriptionOffer });
        return subscriptionOffer;
      },
      properties: [],
      property: null,
    }),
    {
      getStorage: () => localStorage,
      name: 'properties-model',
    },
  ),
);

export function usePropertiesModel(): PropertiesModelProps & PropertiesModelActions;
export function usePropertiesModel<T>(
  selector: (state: PropertiesModelProps & PropertiesModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function usePropertiesModel<T>(
  selector?: (state: PropertiesModelProps & PropertiesModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(PropertiesModel, selector!, equals);
}
